import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../utils/cn';
import { ChevronDown, X, Plus } from 'lucide-react';

const MultiSelect = ({ 
  options = [], 
  value = [], 
  onChange = null, 
  placeholder = "Select options...", 
  disabled = false,
  className = '',
  label,
  required = false,
  error,
  description,
  searchable = false,
  clearable = false,
  allowCustom = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [customInput, setCustomInput] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const dropdownRef = useRef(null);
  const customInputRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = searchable 
    ? options?.filter(option =>
        option?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      )
    : options;

  // Get selected options for display - handle both predefined and custom options
  const getSelectedOptions = () => {
    if (!value || value?.length === 0) return [];
    
    return value?.map(val => {
      // Try to find in predefined options first
      const predefinedOption = options?.find(opt => opt?.value === val);
      if (predefinedOption) return predefinedOption;
      
      // If not found, treat as custom option
      return {
        value: val,
        label: val,
        isCustom: true
      };
    });
  };

  const selectedOptions = getSelectedOptions();

  const handleOptionToggle = (optionValue) => {
    if (!onChange) return;
    
    const newValue = value?.includes(optionValue)
      ? value?.filter(v => v !== optionValue)
      : [...(value || []), optionValue];
    onChange(newValue);
  };

  const handleRemoveTag = (optionValue, e) => {
    e?.stopPropagation();
    if (!onChange) return;
    
    const newValue = (value || [])?.filter(v => v !== optionValue);
    onChange(newValue);
  };

  const handleClearAll = (e) => {
    e?.stopPropagation();
    if (!onChange) return;
    onChange([]);
  };

  const handleAddCustom = () => {
    if (!customInput?.trim() || !onChange) return;
    
    const trimmedInput = customInput?.trim();
    
    // Check if custom value already exists
    if (value?.includes(trimmedInput)) {
      setCustomInput('');
      setShowCustomInput(false);
      return;
    }
    
    // Add custom value
    const newValue = [...(value || []), trimmedInput];
    onChange(newValue);
    setCustomInput('');
    setShowCustomInput(false);
  };

  const handleCustomInputKeyDown = (e) => {
    if (e?.key === 'Enter') {
      e?.preventDefault();
      handleAddCustom();
    } else if (e?.key === 'Escape') {
      setCustomInput('');
      setShowCustomInput(false);
    }
  };

  const getDisplayText = () => {
    if (!selectedOptions || selectedOptions?.length === 0) return placeholder;
    if (selectedOptions?.length === 1) return `1 item selected`;
    return `${selectedOptions?.length} items selected`;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setShowCustomInput(false);
        setCustomInput('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus custom input when it becomes visible
  useEffect(() => {
    if (showCustomInput && customInputRef?.current) {
      customInputRef?.current?.focus();
    }
  }, [showCustomInput]);

  return (
    <div className={cn("relative", className)}>
      {/* Label */}
      {label && (
        <label className={cn(
          "text-sm font-medium leading-none mb-2 block",
          error ? "text-destructive" : "text-foreground"
        )}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}

      {/* Select Button */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          className={cn(
            // Enhanced multi-select field styling with clear border and improved visibility
            "flex h-10 w-full items-center justify-between rounded-md",
            "border-2 border-input bg-background text-foreground px-3 py-2 text-sm",
            // Strong focus states
            "focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20",
            // Better hover states
            "hover:border-primary/60 hover:bg-accent/30",
            // Disabled and error states
            "disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-destructive focus:ring-destructive/20",
            "transition-all duration-200"
          )}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
        >
          <span className={cn(
            "truncate text-left flex-1",
            (!selectedOptions || selectedOptions?.length === 0) && "text-muted-foreground"
          )}>
            {getDisplayText()}
          </span>
          <div className="flex items-center space-x-1 ml-2">
            {clearable && selectedOptions && selectedOptions?.length > 0 && (
              <button
                onClick={handleClearAll}
                className="text-muted-foreground hover:text-destructive p-1 rounded transition-colors"
                type="button"
                title="Clear all selections"
              >
                <X size={14} />
              </button>
            )}
            <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
          </div>
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div 
            className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-auto"
          >
            {/* Search - Only show if searchable prop is true */}
            {searchable && (
              <div className="p-2 border-b">
                <input
                  type="text"
                  className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Search amenities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                />
              </div>
            )}

            {/* Custom Input - Show when allowCustom is true and user wants to add custom */}
            {allowCustom && showCustomInput && (
              <div className="p-2 border-b bg-blue-50">
                <div className="flex items-center space-x-2">
                  <input
                    ref={customInputRef}
                    type="text"
                    className="flex-1 px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder="Enter custom amenity..."
                    value={customInput}
                    onChange={(e) => setCustomInput(e?.target?.value)}
                    onKeyDown={handleCustomInputKeyDown}
                  />
                  <button
                    onClick={handleAddCustom}
                    className="px-3 py-2 bg-primary text-primary-foreground text-sm rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
                    disabled={!customInput?.trim()}
                    type="button"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => {
                      setShowCustomInput(false);
                      setCustomInput('');
                    }}
                    className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 transition-colors"
                    type="button"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Add Custom Button - Show when allowCustom is true and custom input is not shown */}
            {allowCustom && !showCustomInput && (
              <div className="p-2 border-b">
                <button
                  onClick={() => setShowCustomInput(true)}
                  className="w-full flex items-center justify-center space-x-2 px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
                  type="button"
                >
                  <Plus size={14} />
                  <span>Add Custom Amenity</span>
                </button>
              </div>
            )}

            {/* Options */}
            <div className="py-1">
              {!filteredOptions || filteredOptions?.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  {searchable && searchTerm ? 'No matching amenities found' : 'No options available'}
                </div>
              ) : (
                filteredOptions?.map((option) => {
                  const isSelected = value?.includes(option?.value);
                  return (
                    <div
                      key={option?.value}
                      className={cn(
                        "px-3 py-2 text-sm cursor-pointer transition-colors",
                        // Enhanced hover and selection states
                        "hover:bg-accent hover:text-accent-foreground",
                        isSelected && "bg-primary/10 text-primary font-medium"
                      )}
                      onClick={() => handleOptionToggle(option?.value)}
                    >
                      <div className="flex items-center justify-between">
                        <span>{option?.label}</span>
                        {isSelected && (
                          <div className="w-4 h-4 rounded border-2 border-primary bg-primary flex items-center justify-center">
                            <div className="w-2 h-2 bg-primary-foreground rounded-sm"></div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Selected Items Tags (Below the field like Perks & Benefits design) */}
      {selectedOptions && selectedOptions?.length > 0 && (
        <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-green-800 font-medium">
              Selected Items ({selectedOptions?.length}):
            </p>
            {clearable && (
              <button
                onClick={(e) => handleClearAll(e)}
                className="text-xs text-green-600 hover:text-green-800 underline focus:outline-none"
                type="button"
              >
                Clear All
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedOptions?.map((option) => (
              <span
                key={option?.value}
                className={cn(
                  "inline-flex items-center px-3 py-1 text-xs font-medium rounded-full border transition-all",
                  option?.isCustom 
                    ? "bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200" :"bg-green-100 text-green-800 border-green-300 hover:bg-green-200"
                )}
              >
                {option?.isCustom && (
                  <span className="mr-1 text-blue-600" title="Custom amenity">
                    ✨
                  </span>
                )}
                {option?.label}
                <button
                  onClick={(e) => handleRemoveTag(option?.value, e)}
                  className={cn(
                    "ml-2 focus:outline-none transition-colors",
                    option?.isCustom 
                      ? "text-blue-600 hover:text-blue-800" :"text-green-600 hover:text-green-800"
                  )}
                  type="button"
                  title={`Remove ${option?.label}`}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Description/Error */}
      {description && !error && (
        <p className="text-sm text-muted-foreground mt-1">{description}</p>
      )}
      {error && (
        <p className="text-sm text-destructive mt-1">{error}</p>
      )}
    </div>
  );
};

export default MultiSelect;